import { useMemo } from 'react';
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { Bucket, ModelComparison, Projection } from '../api';
import type { VizColors } from '../theme';

/**
 * Chart marks.
 *
 * Conventions applied throughout: a single series carries no legend (the title
 * names it), two or more always do; grid and axes are recessive; bars have 4px
 * rounded tops anchored to the baseline; lines are 2px with 8px markers; every
 * chart has a hover tooltip.
 *
 * There is deliberately no dual-axis chart anywhere in this app. Two measures
 * on two scales in one frame invite a comparison the geometry does not support;
 * DataVantageHub's "Revenue vs Users" chart did exactly that.
 */

const AXIS_TICK = { fontSize: 12 };

function formatNumber(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(1)}B`;
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (abs >= 10_000) return `${(value / 1000).toFixed(1)}k`;
  return value.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

function truncate(value: string, max = 18): string {
  return value.length > max ? `${value.slice(0, max - 1)}…` : value;
}

interface TooltipRow {
  name?: string | number;
  value?: number | string | (number | string)[];
  color?: string;
}

function ChartTooltip({
  active,
  label,
  payload,
  colors,
}: {
  active?: boolean;
  label?: string | number;
  payload?: TooltipRow[];
  colors: VizColors;
}) {
  if (active !== true || payload === undefined || payload.length === 0) return null;

  return (
    <div
      style={{
        background: colors.surface,
        border: `1px solid ${colors.axis}`,
        borderRadius: 7,
        padding: '8px 10px',
        fontSize: 12,
        color: colors.ink,
        boxShadow: '0 2px 10px rgba(0,0,0,0.14)',
      }}
    >
      <div style={{ fontWeight: 620, marginBottom: 4 }}>{String(label ?? '')}</div>
      {payload.map((row, index) => (
        <div key={index} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: 2,
              background: row.color ?? colors.series1,
              flex: '0 0 auto',
            }}
          />
          <span style={{ color: colors.muted }}>{String(row.name ?? '')}</span>
          <span style={{ marginLeft: 'auto', fontVariantNumeric: 'tabular-nums' }}>
            {Array.isArray(row.value)
              ? row.value.map((v) => (typeof v === 'number' ? formatNumber(v) : String(v))).join(' – ')
              : typeof row.value === 'number'
                ? row.value.toLocaleString('en-US', { maximumFractionDigits: 2 })
                : String(row.value ?? '')}
          </span>
        </div>
      ))}
    </div>
  );
}

export function CategoryChart({
  buckets,
  otherBucket,
  measureLabel,
  colors,
  height = 260,
}: {
  buckets: Bucket[];
  otherBucket?: Bucket | null;
  measureLabel: string;
  colors: VizColors;
  height?: number;
}) {
  // The folded remainder is shown, not dropped — a bar chart that silently
  // omits the tail misrepresents the total.
  const data = useMemo(
    () => [...buckets, ...(otherBucket ? [otherBucket] : [])].map((b) => ({ ...b, short: truncate(b.key) })),
    [buckets, otherBucket],
  );

  if (data.length === 0) return <p className="muted">No rows match.</p>;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 8, right: 8, bottom: 4, left: 0 }}>
        <CartesianGrid stroke={colors.grid} vertical={false} />
        <XAxis
          dataKey="short"
          tick={AXIS_TICK}
          stroke={colors.axis}
          interval={0}
          angle={data.length > 6 ? -30 : 0}
          textAnchor={data.length > 6 ? 'end' : 'middle'}
          height={data.length > 6 ? 62 : 30}
        />
        <YAxis tick={AXIS_TICK} stroke={colors.axis} tickFormatter={formatNumber} width={58} />
        <Tooltip cursor={{ fill: colors.grid, opacity: 0.5 }} content={<ChartTooltip colors={colors} />} />
        <Bar dataKey="value" name={measureLabel} fill={colors.series1} radius={[4, 4, 0, 0]} maxBarSize={54} />
      </BarChart>
    </ResponsiveContainer>
  );
}

export function TrendChart({
  buckets,
  measureLabel,
  colors,
  height = 260,
  area = false,
}: {
  buckets: Bucket[];
  measureLabel: string;
  colors: VizColors;
  height?: number;
  area?: boolean;
}) {
  const data = useMemo(() => buckets.map((b) => ({ ...b, short: b.key })), [buckets]);
  if (data.length === 0) return <p className="muted">No rows match.</p>;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={data} margin={{ top: 8, right: 8, bottom: 4, left: 0 }}>
        <CartesianGrid stroke={colors.grid} vertical={false} />
        <XAxis dataKey="short" tick={AXIS_TICK} stroke={colors.axis} minTickGap={24} />
        <YAxis tick={AXIS_TICK} stroke={colors.axis} tickFormatter={formatNumber} width={58} />
        <Tooltip content={<ChartTooltip colors={colors} />} />
        {area && (
          <Area
            dataKey="value"
            name={measureLabel}
            stroke="none"
            fill={colors.band}
            isAnimationActive={false}
          />
        )}
        <Line
          type="monotone"
          dataKey="value"
          name={measureLabel}
          stroke={colors.series1}
          strokeWidth={2}
          dot={data.length <= 40 ? { r: 4, strokeWidth: 0, fill: colors.series1 } : false}
          activeDot={{ r: 5 }}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

export function ShareChart({
  buckets,
  colors,
  height = 260,
}: {
  buckets: Bucket[];
  colors: VizColors;
  height?: number;
}) {
  // Three slices at most, plus a remainder. A pie with nine wedges cannot be
  // read; the palette only validates three hues against each other anyway.
  const data = useMemo(() => {
    const sorted = [...buckets].sort((a, b) => b.value - a.value);
    const top = sorted.slice(0, 3);
    const rest = sorted.slice(3);
    const remainder = rest.reduce((sum, b) => sum + b.value, 0);
    return remainder > 0
      ? [...top, { key: `Other (${rest.length})`, value: remainder, count: 0 }]
      : top;
  }, [buckets]);

  const palette = [colors.series1, colors.series2, colors.series3, colors.axis];
  if (data.length === 0) return <p className="muted">No rows match.</p>;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Tooltip content={<ChartTooltip colors={colors} />} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <Pie
          data={data}
          dataKey="value"
          nameKey="key"
          innerRadius="45%"
          outerRadius="72%"
          paddingAngle={2}
          stroke={colors.surface}
          strokeWidth={2}
        >
          {data.map((_, index) => (
            <Cell key={index} fill={palette[index % palette.length]} />
          ))}
        </Pie>
      </PieChart>
    </ResponsiveContainer>
  );
}

export function ScatterPlot({
  points,
  xLabel,
  yLabel,
  colors,
  height = 260,
}: {
  points: { x: number; y: number }[];
  xLabel: string;
  yLabel: string;
  colors: VizColors;
  height?: number;
}) {
  if (points.length === 0) return <p className="muted">No rows match.</p>;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ScatterChart margin={{ top: 8, right: 12, bottom: 20, left: 0 }}>
        <CartesianGrid stroke={colors.grid} />
        <XAxis
          dataKey="x"
          name={xLabel}
          type="number"
          tick={AXIS_TICK}
          stroke={colors.axis}
          tickFormatter={formatNumber}
        />
        <YAxis
          dataKey="y"
          name={yLabel}
          type="number"
          tick={AXIS_TICK}
          stroke={colors.axis}
          tickFormatter={formatNumber}
          width={58}
        />
        <Tooltip content={<ChartTooltip colors={colors} />} />
        <Scatter data={points} fill={colors.series1} fillOpacity={0.7} />
      </ScatterChart>
    </ResponsiveContainer>
  );
}

/**
 * History, projection, and the prediction interval as a band.
 *
 * The band is the point of the chart. A forecast line without one implies a
 * precision the model does not have — DataDashMaster drew two bare lines, the
 * second produced by adding random jitter to the first.
 */
export function ForecastChart({
  forecast,
  measureLabel,
  colors,
  height = 300,
}: {
  forecast: Projection;
  measureLabel: string;
  colors: VizColors;
  height?: number;
}) {
  const data = useMemo(() => {
    const day = (iso: string) => iso.slice(0, 10);

    const history = forecast.history.map((point) => ({
      at: day(point.at),
      actual: point.actual,
      fitted: point.fitted,
      projected: undefined as number | undefined,
      band: undefined as [number, number] | undefined,
    }));

    // Repeat the last actual as the projection's first point so the two lines
    // meet instead of leaving a visual gap the data does not have.
    const last = forecast.history[forecast.history.length - 1];
    const bridge =
      last === undefined
        ? []
        : [
            {
              at: day(last.at),
              actual: last.actual,
              fitted: last.fitted,
              projected: last.fitted,
              band: [last.fitted, last.fitted] as [number, number],
            },
          ];

    const projected = forecast.points.map((point) => ({
      at: day(point.at),
      actual: undefined as number | undefined,
      fitted: undefined as number | undefined,
      projected: point.value,
      band: [point.lower, point.upper] as [number, number],
    }));

    return [...history.slice(0, -1), ...bridge, ...projected];
  }, [forecast]);

  const interval = `${Math.round(forecast.confidenceLevel * 100)}% interval`;
  const dense = forecast.history.length > 60;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={data} margin={{ top: 8, right: 8, bottom: 4, left: 0 }}>
        <CartesianGrid stroke={colors.grid} vertical={false} />
        <XAxis dataKey="at" tick={AXIS_TICK} stroke={colors.axis} minTickGap={34} />
        <YAxis tick={AXIS_TICK} stroke={colors.axis} tickFormatter={formatNumber} width={58} />
        <Tooltip content={<ChartTooltip colors={colors} />} />
        <Legend wrapperStyle={{ fontSize: 12 }} />

        {/* Where history ends and extrapolation begins — the single most
            important boundary on the chart. */}
        {forecast.history.length > 0 && (
          <ReferenceLine
            x={forecast.history[forecast.history.length - 1]!.at.slice(0, 10)}
            stroke={colors.axis}
            strokeDasharray="3 3"
          />
        )}

        <Area
          dataKey="band"
          name={interval}
          stroke="none"
          fill={colors.band}
          isAnimationActive={false}
          connectNulls
        />
        <Line
          dataKey="actual"
          name={measureLabel}
          stroke={colors.series1}
          strokeWidth={2}
          dot={false}
          connectNulls
        />
        {/* The fitted line makes the model's error visible against the actual
            series, instead of hiding it behind a smooth projection. */}
        <Line
          dataKey="fitted"
          name="Model fit"
          stroke={colors.series3}
          strokeWidth={1.5}
          strokeOpacity={0.85}
          dot={false}
          connectNulls
        />
        <Line
          dataKey="projected"
          name="Projected"
          stroke={colors.series1}
          strokeWidth={2}
          strokeDasharray="6 4"
          dot={dense ? false : { r: 3.5, strokeWidth: 0, fill: colors.series1 }}
          connectNulls
        />

        {/* Where the fitted trend bends. A bend the chart does not mark is a
            bend a reader has to take the table's word for. */}
        {forecast.changepoints.map((point) => (
          <ReferenceLine
            key={point.at}
            x={point.at.slice(0, 10)}
            stroke={colors.series2}
            strokeOpacity={0.55}
            strokeDasharray="2 3"
          />
        ))}
      </ComposedChart>
    </ResponsiveContainer>
  );
}

/**
 * The fit, taken apart: trend on its own, then each seasonal cycle.
 *
 * This is what makes a decomposable model auditable rather than a black line. A
 * reader who can see that "yearly seasonality" is a smooth ±5% wave, not a
 * spike fitted to one December, can decide how much to trust the projection.
 */
export function ComponentsChart({
  projection,
  colors,
  height = 260,
}: {
  projection: Projection;
  colors: VizColors;
  height?: number;
}) {
  const names = useMemo(
    () => projection.seasonalities.map((s) => s.name),
    [projection.seasonalities],
  );

  const data = useMemo(
    () =>
      projection.history.map((point) => {
        const row: Record<string, number | string> = {
          at: point.at.slice(0, 10),
          trend: point.components.trend,
        };
        for (const name of names) row[name] = point.components.seasonal[name] ?? 0;
        return row;
      }),
    [projection.history, names],
  );

  const seasonalPalette = [colors.series2, colors.series3];

  return (
    <>
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={data} margin={{ top: 8, right: 8, bottom: 4, left: 0 }}>
          <CartesianGrid stroke={colors.grid} vertical={false} />
          <XAxis dataKey="at" tick={AXIS_TICK} stroke={colors.axis} minTickGap={34} />
          <YAxis tick={AXIS_TICK} stroke={colors.axis} tickFormatter={formatNumber} width={58} />
          <Tooltip content={<ChartTooltip colors={colors} />} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Line dataKey="trend" name="Trend" stroke={colors.series1} strokeWidth={2} dot={false} />
        </ComposedChart>
      </ResponsiveContainer>

      {names.length > 0 && (
        <ResponsiveContainer width="100%" height={height * 0.72}>
          <ComposedChart data={data} margin={{ top: 8, right: 8, bottom: 4, left: 0 }}>
            <CartesianGrid stroke={colors.grid} vertical={false} />
            <XAxis dataKey="at" tick={AXIS_TICK} stroke={colors.axis} minTickGap={34} />
            <YAxis tick={AXIS_TICK} stroke={colors.axis} tickFormatter={formatNumber} width={58} />
            <Tooltip content={<ChartTooltip colors={colors} />} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {/* Seasonality is a deviation from the trend, so zero is meaningful. */}
            <ReferenceLine y={0} stroke={colors.axis} />
            {names.map((name, index) => (
              <Line
                key={name}
                dataKey={name}
                name={`${name} seasonality`}
                stroke={seasonalPalette[index % seasonalPalette.length]}
                strokeWidth={1.75}
                dot={false}
              />
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </>
  );
}

/**
 * Residuals over time: how far the fitted model missed each real observation.
 *
 * The zero line is the whole point. A healthy fit leaves noise scattered either
 * side of it; a long run above or below means the model is systematically
 * missing something — exactly what an R² alone cannot show.
 */
export function ResidualChart({
  forecast,
  colors,
  height = 220,
}: {
  forecast: Projection;
  colors: VizColors;
  height?: number;
}) {
  const data = useMemo(
    () =>
      forecast.history.map((point) => ({
        at: point.at.slice(0, 10),
        residual: point.residual,
      })),
    [forecast.history],
  );

  if (data.length === 0) return <p className="muted">No fitted history to plot residuals for.</p>;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={data} margin={{ top: 8, right: 8, bottom: 4, left: 0 }}>
        <CartesianGrid stroke={colors.grid} vertical={false} />
        <XAxis dataKey="at" tick={AXIS_TICK} stroke={colors.axis} minTickGap={34} />
        <YAxis tick={AXIS_TICK} stroke={colors.axis} tickFormatter={formatNumber} width={58} />
        <Tooltip content={<ChartTooltip colors={colors} />} />
        <ReferenceLine y={0} stroke={colors.axis} />
        <Bar dataKey="residual" name="Residual" isAnimationActive={false} radius={[2, 2, 0, 0]}>
          {data.map((point, index) => (
            <Cell key={index} fill={point.residual >= 0 ? colors.series3 : colors.series2} />
          ))}
        </Bar>
      </ComposedChart>
    </ResponsiveContainer>
  );
}

/**
 * The honest picture of a forecast: what the winning model would have predicted
 * for data it had not seen, against what actually happened, across every held-out
 * fold. This is the chart a projection alone cannot draw.
 */
export function BacktestChart({
  comparison,
  colors,
  height = 260,
}: {
  comparison: ModelComparison;
  colors: VizColors;
  height?: number;
}) {
  const best =
    comparison.evaluated.find((entry) => entry.model === comparison.best) ?? comparison.evaluated[0];

  const data = useMemo(() => {
    if (best === undefined) return [];
    const rows: { at: string; actual: number; predicted: number; band: [number, number] }[] = [];
    for (const fold of best.folds) {
      for (const point of fold.points) {
        rows.push({
          at: point.at.slice(0, 10),
          actual: point.actual,
          predicted: point.predicted,
          band: [point.lower, point.upper],
        });
      }
    }
    return rows.sort((a, b) => a.at.localeCompare(b.at));
  }, [best]);

  if (data.length === 0) return null;

  return (
    <div style={{ marginTop: 12 }}>
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={data} margin={{ top: 8, right: 8, bottom: 4, left: 0 }}>
          <CartesianGrid stroke={colors.grid} vertical={false} />
          <XAxis dataKey="at" tick={AXIS_TICK} stroke={colors.axis} minTickGap={34} />
          <YAxis tick={AXIS_TICK} stroke={colors.axis} tickFormatter={formatNumber} width={58} />
          <Tooltip content={<ChartTooltip colors={colors} />} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Area
            dataKey="band"
            name="Interval"
            stroke="none"
            fill={colors.band}
            connectNulls={false}
            isAnimationActive={false}
          />
          <Line
            dataKey="actual"
            name="Actual"
            stroke={colors.series1}
            strokeWidth={2}
            dot={false}
            connectNulls={false}
            isAnimationActive={false}
          />
          <Line
            dataKey="predicted"
            name="Predicted"
            stroke={colors.series3}
            strokeWidth={2}
            strokeDasharray="5 4"
            dot={false}
            connectNulls={false}
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
